import { useEffect, useState } from "react";
import { useOutletContext, useSearchParams } from "react-router-dom";
import {
  Palette, Plus, Trash2, Copy, Download, Loader2, ExternalLink, Type, Scissors,
  Image as ImageIcon, Film, SlidersHorizontal, Link2, RefreshCw, Search, Unplug, Cloud, Share2, Users,
} from "lucide-react";
import { SocialGroups } from "@/components/SocialGroups";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { FileUpload, resolveMediaUrl } from "@/components/FileUpload";
import { SharePointPickerDialog } from "@/components/SharePointPickerDialog";
import { BrandBulkUpload } from "@/components/BrandBulkUpload";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const cls = "w-full h-10 px-3 rounded-lg border border-neutral-200 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-fuchsia-500/30 focus:border-fuchsia-400";

export async function copyToClipboard(text, successMsg = "Kopiert") {
  try {
    await navigator.clipboard.writeText(text);
    toast.success(successMsg);
  } catch {
    toast.error("Kopieren nicht erlaubt — bitte manuell markieren");
  }
}

const CATEGORIES = [
  { key: "logo", label: "Logos & Grafiken", icon: ImageIcon },
  { key: "color", label: "Farben", icon: Palette },
  { key: "font", label: "Schriften", icon: Type },
  { key: "video", label: "Video-Assets", icon: Film },
  { key: "preset", label: "Presets & Vorlagen", icon: SlidersHorizontal },
  { key: "canva", label: "Canva", icon: Link2 },
  { key: "groups", label: "Gruppen", icon: Users },
];

export default function BrandHub() {
  const { accountKey } = useOutletContext();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [searchParams, setSearchParams] = useSearchParams();
  const [tab, setTab] = useState("logo");
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [query, setQuery] = useState("");

  const load = () =>
    api.get("/brand/assets", { params: { account: accountKey } })
      .then(({ data }) => { setAssets(data); setLoading(false); })
      .catch(() => setLoading(false));

  useEffect(() => { if (accountKey) { setLoading(true); load(); } }, [accountKey]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const st = searchParams.get("canva");
    if (!st) return;
    if (st === "connected") toast.success("Canva erfolgreich verbunden ✨");
    else toast.error(searchParams.get("msg") || "Canva-Verbindung fehlgeschlagen");
    setTab("canva");
    setSearchParams({}, { replace: true });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const remove = async (id) => {
    try {
      await api.delete(`/brand/assets/${id}`);
      toast.success("Asset gelöscht");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Löschen fehlgeschlagen");
    }
  };

  const byCategory = (cat) => assets.filter((a) => a.category === cat);

  const q = query.trim().toLowerCase();
  const searchResults = q
    ? assets.filter((a) =>
        [a.name, a.description, a.hex, a.text_content].some((v) => v && v.toLowerCase().includes(q))
      )
    : [];

  return (
    <div data-testid="brand-hub" className="max-w-6xl">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div>
          <p className="text-[11px] font-bold uppercase tracking-[0.15em] text-neutral-400 flex items-center gap-1.5">
            <Palette className="h-3.5 w-3.5" /> Brand Hub
          </p>
          <h1 className="font-head font-bold text-2xl md:text-3xl text-neutral-900 mt-1">Ein Look für alle</h1>
          <p className="text-sm text-neutral-500 mt-1">
            Logos, CI-Farben, Schriften, Blenden & Presets — zentral gepflegt, für alle Creator identisch.
          </p>
        </div>
        {tab !== "canva" && (
          <div className="shrink-0 flex flex-wrap items-center gap-2">
            <button
              data-testid="brand-bulk-btn"
              onClick={() => setBulkOpen(true)}
              className="inline-flex items-center gap-2 border border-neutral-300 text-neutral-800 text-sm font-medium px-4 py-2.5 rounded-lg hover:bg-neutral-50"
            >
              <Plus className="h-4 w-4" /> Mehrere hochladen
            </button>
            <button
              data-testid="brand-add-btn"
              onClick={() => setAddOpen(true)}
              className="inline-flex items-center gap-2 bg-neutral-900 text-white text-sm font-medium px-4 py-2.5 rounded-lg hover:bg-neutral-800"
            >
              <Plus className="h-4 w-4" /> Asset hinzufügen
            </button>
          </div>
        )}
      </div>

      <div className="relative mt-5 max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-400 pointer-events-none" />
        <input
          data-testid="brand-search-input"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Assets durchsuchen — Name, Beschreibung, Hex…"
          className="w-full h-10 pl-9 pr-9 rounded-lg border border-neutral-200 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-fuchsia-500/30 focus:border-fuchsia-400"
        />
        {query && (
          <button
            data-testid="brand-search-clear"
            onClick={() => setQuery("")}
            className="absolute right-2 top-1/2 -translate-y-1/2 h-6 w-6 rounded-md flex items-center justify-center text-neutral-400 hover:text-neutral-700 hover:bg-neutral-100"
          >
            ×
          </button>
        )}
      </div>

      <div className="flex flex-wrap gap-1.5 mt-4 border-b border-neutral-200 pb-px">
        {CATEGORIES.map((c) => (
          <button
            key={c.key}
            data-testid={`brand-tab-${c.key}`}
            onClick={() => setTab(c.key)}
            className={`inline-flex items-center gap-1.5 px-3.5 py-2 text-sm font-medium rounded-t-lg border-b-2 transition-colors ${
              tab === c.key
                ? "border-fuchsia-500 text-neutral-900 bg-fuchsia-50/60"
                : "border-transparent text-neutral-500 hover:text-neutral-800"
            }`}
          >
            <c.icon className="h-4 w-4" /> {c.label}
            {c.key !== "canva" && c.key !== "groups" && byCategory(c.key).length > 0 && (
              <span className="text-[11px] font-semibold text-neutral-400">{byCategory(c.key).length}</span>
            )}
          </button>
        ))}
      </div>

      <div className="mt-6">
        {loading ? (
          <div className="py-16 flex justify-center text-neutral-400"><Loader2 className="h-6 w-6 animate-spin" /></div>
        ) : q ? (
          <SearchResults results={searchResults} query={query} isAdmin={isAdmin} onDelete={remove} onRefresh={load} />
        ) : tab === "canva" ? (
          <CanvaSection
            accountKey={accountKey}
            isAdmin={isAdmin}
            templates={byCategory("canva_template")}
            onTemplatesChanged={load}
            onRemoveTemplate={remove}
          />
        ) : tab === "groups" ? (
          <SocialGroups accountKey={accountKey} isAdmin={isAdmin} />
        ) : tab === "video" ? (
          <VideoAssetSection accountKey={accountKey} items={byCategory("video")} isAdmin={isAdmin} onDelete={remove} onChanged={load} />
        ) : (
          <AssetGrid category={tab} items={byCategory(tab)} isAdmin={isAdmin} onDelete={remove} onRefresh={load} accountKey={accountKey} />
        )}
      </div>

      <AddAssetDialog
        open={addOpen}
        onOpenChange={setAddOpen}
        accountKey={accountKey}
        defaultCategory={tab === "canva" || tab === "groups" ? "logo" : tab}
        onCreated={() => { setAddOpen(false); load(); }}
      />

      <BrandBulkUpload open={bulkOpen} onOpenChange={setBulkOpen} accountKey={accountKey} onDone={load} />
    </div>
  );
}

function SearchResults({ results, query, isAdmin, onDelete, onRefresh }) {
  if (results.length === 0) {
    return (
      <p data-testid="brand-search-empty" className="text-sm text-neutral-400 py-12 text-center">
        Keine Assets für „{query}" gefunden.
      </p>
    );
  }
  const colors = results.filter((a) => a.category === "color");
  const files = results.filter((a) => a.category !== "color");
  return (
    <div data-testid="brand-search-results" className="space-y-5">
      <p className="text-xs font-semibold text-neutral-500 uppercase tracking-wider">
        {results.length} Treffer für „{query}"
      </p>
      {colors.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {colors.map((a) => <ColorCard key={a.id} asset={a} isAdmin={isAdmin} onDelete={onDelete} />)}
        </div>
      )}
      {files.length > 0 && (
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
          {files.map((a) => <FileAssetCard key={a.id} asset={a} isAdmin={isAdmin} onDelete={onDelete} onRefresh={onRefresh} />)}
        </div>
      )}
    </div>
  );
}

function VideoAssetSection({ accountKey, items, isAdmin, onDelete, onChanged }) {
  const [cats, setCats] = useState({ defaults: [], custom: [] });
  const [sub, setSub] = useState("all");
  const [selected, setSelected] = useState(new Set());
  const toggleSel = (id) => setSelected((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });

  const loadCats = () =>
    api.get("/brand/video-categories", { params: { account: accountKey } }).then(({ data }) => setCats(data)).catch(() => {});
  useEffect(() => { loadCats(); }, [accountKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const allNames = [...cats.defaults, ...cats.custom.map((c) => c.name)];
  const subOf = (a) => (a.subcategory && allNames.includes(a.subcategory) ? a.subcategory : "Sonstiges");
  const countOf = (name) => items.filter((a) => subOf(a) === name).length;
  const filtered = sub === "all" ? items : items.filter((a) => subOf(a) === sub);

  const addCat = async () => {
    const name = window.prompt("Name der neuen Video-Kategorie:");
    if (!name?.trim()) return;
    try {
      await api.post("/brand/video-categories", { account_key: accountKey, name: name.trim() });
      toast.success(`Kategorie „${name.trim()}" angelegt ✅`);
      loadCats();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Kategorie konnte nicht angelegt werden");
    }
  };

  const removeCat = async (c) => {
    if (!window.confirm(`Kategorie „${c.name}" löschen? Videos darin wandern nach „Sonstiges".`)) return;
    try {
      await api.delete(`/brand/video-categories/${c.id}`);
      if (sub === c.name) setSub("all");
      loadCats();
      onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Löschen fehlgeschlagen");
    }
  };

  const changeSub = async (asset, value) => {
    try {
      await api.patch(`/brand/assets/${asset.id}`, { subcategory: value === "Sonstiges" ? "" : value });
      onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Umsortieren fehlgeschlagen");
    }
  };

  const chip = (key, label, count, custom = null) => (
    <span key={key} className="inline-flex items-center">
      <button
        data-testid={`brand-video-cat-${key}`}
        onClick={() => setSub(key)}
        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold border transition ${
          sub === key ? "bg-neutral-900 text-white border-neutral-900" : "bg-white text-neutral-600 border-neutral-200 hover:border-neutral-400"
        } ${custom ? "rounded-r-none" : ""}`}
      >
        {label}
        {count > 0 && <span className={sub === key ? "text-white/60" : "text-neutral-400"}>{count}</span>}
      </button>
      {custom && isAdmin && (
        <button
          data-testid={`brand-video-cat-delete-${custom.id}`}
          onClick={() => removeCat(custom)}
          title="Kategorie löschen"
          className="h-[30px] px-1.5 rounded-r-full border border-l-0 border-neutral-200 text-neutral-300 hover:text-red-600 hover:bg-red-50 text-xs"
        >
          ×
        </button>
      )}
    </span>
  );

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-1.5 items-center" data-testid="brand-video-cats">
        {chip("all", "Alle", items.length)}
        {cats.defaults.map((n) => chip(n, n, countOf(n)))}
        {cats.custom.map((c) => chip(c.name, c.name, countOf(c.name), c))}
        {chip("Sonstiges", "Sonstiges", countOf("Sonstiges"))}
        {isAdmin && (
          <button data-testid="brand-video-cat-add" onClick={addCat}
            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-bold border border-dashed border-neutral-300 text-neutral-500 hover:border-neutral-500 hover:text-neutral-800">
            <Plus className="h-3 w-3" /> Kategorie
          </button>
        )}
      </div>
      {filtered.length === 0 ? (
        <p data-testid="brand-video-empty" className="text-sm text-neutral-400 py-10 text-center">Keine Videos in dieser Kategorie.</p>
      ) : (
        <>
          <BulkDownloadBar accountKey={accountKey} selected={selected} onClear={() => setSelected(new Set())} assets={items} />
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
            {filtered.map((a) => (
              <FileAssetCard key={a.id} asset={a} isAdmin={isAdmin} onDelete={onDelete}
                videoCatOptions={allNames} onSubcategory={changeSub}
                selected={selected.has(a.id)} onToggleSelect={a.media_url ? () => toggleSel(a.id) : null} />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

const MIME_EXT = {
  "image/png": ".png", "image/jpeg": ".jpg", "image/webp": ".webp", "image/gif": ".gif",
  "image/svg+xml": ".svg", "video/mp4": ".mp4", "video/quicktime": ".mov", "application/pdf": ".pdf",
};

export function BulkDownloadBar({ accountKey, selected, onClear, assets = [] }) {
  const [busy, setBusy] = useState(false);
  const [shareBusy, setShareBusy] = useState(false);
  const canShare = typeof navigator !== "undefined" && typeof navigator.canShare === "function";

  const shareToDevice = async () => {
    setShareBusy(true);
    try {
      const chosen = assets.filter((a) => selected.has(a.id) && a.media_url);
      const files = [];
      for (const a of chosen) {
        const resp = await fetch(resolveMediaUrl(a.media_url));
        const blob = await resp.blob();
        const ext = MIME_EXT[blob.type] || "";
        const base = (a.name || "asset").replace(/[^\w\-. ]/g, "").trim() || "asset";
        files.push(new File([blob], `${base}${ext}`, { type: blob.type }));
      }
      if (!files.length || !navigator.canShare({ files })) {
        toast.error("Direktes Speichern wird hier nicht unterstützt — bitte »Als ZIP« nutzen");
        return;
      }
      await navigator.share({ files });
      toast.success("Im Teilen-Menü »Sichern« wählen — fertig ✅");
      onClear();
    } catch (e) {
      if (e?.name !== "AbortError") toast.error("Speichern nicht möglich — bitte »Als ZIP« nutzen");
    } finally {
      setShareBusy(false);
    }
  };

  const run = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(
        "/brand/assets/download-zip",
        { account_key: accountKey, asset_ids: [...selected] },
        { responseType: "blob", timeout: 300000 },
      );
      const blobUrl = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = "brand-assets.zip";
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(blobUrl), 30000);
      toast.success(`${selected.size} Datei${selected.size === 1 ? "" : "en"} als ZIP heruntergeladen ✅`);
      onClear();
    } catch {
      toast.error("Download fehlgeschlagen — Auswahl evtl. zu groß (max. 500 MB)");
    } finally {
      setBusy(false);
    }
  };
  if (selected.size === 0) return null;
  return (
    <div data-testid="brand-bulk-bar" className="sticky top-0 z-20 flex items-center gap-2 flex-wrap bg-fuchsia-50 border border-fuchsia-200 rounded-xl px-3.5 py-2.5">
      <span className="text-[12px] font-bold text-fuchsia-800">
        {selected.size} ausgewählt
      </span>
      {canShare && (
        <button
          data-testid="brand-bulk-share"
          onClick={shareToDevice}
          disabled={shareBusy || busy}
          className="h-8 px-3 rounded-lg bg-white border border-fuchsia-300 text-fuchsia-700 text-[12px] font-bold hover:bg-fuchsia-100 flex items-center gap-1.5 disabled:opacity-60"
        >
          {shareBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Share2 className="h-3.5 w-3.5" />}
          {shareBusy ? "Lädt…" : "In Fotos/Dateien sichern"}
        </button>
      )}
      <button
        data-testid="brand-bulk-download"
        onClick={run}
        disabled={busy}
        className="h-8 px-3 rounded-lg bg-fuchsia-600 text-white text-[12px] font-bold hover:bg-fuchsia-700 flex items-center gap-1.5 disabled:opacity-60"
      >
        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
        {busy ? "Erstellt ZIP…" : "Als ZIP herunterladen"}
      </button>
      <button
        data-testid="brand-bulk-clear"
        onClick={onClear}
        disabled={busy}
        className="ml-auto h-8 w-8 rounded-lg hover:bg-fuchsia-100 text-fuchsia-400 hover:text-fuchsia-700"
        title="Auswahl aufheben"
      >
        ×
      </button>
    </div>
  );
}

function AssetGrid({ category, items, isAdmin, onDelete, onRefresh, accountKey }) {
  const [selected, setSelected] = useState(new Set());
  const toggleSel = (id) => setSelected((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  if (items.length === 0) {
    return (
      <p data-testid="brand-empty" className="text-sm text-neutral-400 py-12 text-center">
        Noch keine Assets in dieser Kategorie. Über „Asset hinzufügen" starten.
      </p>
    );
  }
  if (category === "color") {
    return (
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {items.map((a) => <ColorCard key={a.id} asset={a} isAdmin={isAdmin} onDelete={onDelete} />)}
      </div>
    );
  }
  return (
    <div className="space-y-3">
      <BulkDownloadBar accountKey={accountKey} selected={selected} onClear={() => setSelected(new Set())} assets={items} />
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
        {items.map((a) => (
          <FileAssetCard key={a.id} asset={a} isAdmin={isAdmin} onDelete={onDelete} onRefresh={onRefresh}
            selected={selected.has(a.id)} onToggleSelect={a.media_url ? () => toggleSel(a.id) : null} />
        ))}
      </div>
    </div>
  );
}

function ColorCard({ asset, isAdmin, onDelete }) {
  const copyHex = () => copyToClipboard(asset.hex, `${asset.hex} kopiert`);
  return (
    <div data-testid={`brand-color-${asset.id}`} className="rounded-xl border border-neutral-200 overflow-hidden bg-white group">
      <button onClick={copyHex} className="w-full h-20 block" style={{ background: asset.hex }} title="Hex kopieren" />
      <div className="p-3 flex items-center justify-between gap-2">
        <div className="min-w-0">
          <p className="text-sm font-semibold text-neutral-900 truncate">{asset.name}</p>
          <p className="text-[12px] font-mono text-neutral-500">{asset.hex}</p>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <button data-testid={`brand-copy-${asset.id}`} onClick={copyHex} className="h-8 w-8 rounded-lg border border-neutral-200 flex items-center justify-center text-neutral-500 hover:bg-neutral-50">
            <Copy className="h-3.5 w-3.5" />
          </button>
          {isAdmin && (
            <button data-testid={`brand-delete-${asset.id}`} onClick={() => onDelete(asset.id)} className="h-8 w-8 rounded-lg border border-neutral-200 flex items-center justify-center text-neutral-400 hover:text-red-600 hover:bg-red-50">
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function FileAssetCard({ asset, isAdmin, onDelete, onRefresh = null, videoCatOptions = null, onSubcategory = null, selected = false, onToggleSelect = null }) {
  const url = asset.media_url ? resolveMediaUrl(asset.media_url) : "";
  const copyText = () => copyToClipboard(asset.text_content, "In Zwischenablage kopiert");
  const [bgBusy, setBgBusy] = useState(false);
  const removeBg = async () => {
    setBgBusy(true);
    try {
      await api.post(`/brand/assets/${asset.id}/remove-bg`);
      toast.success("Freigestellt ✂️ — als neues Asset gespeichert");
      onRefresh?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Freistellen fehlgeschlagen");
    } finally {
      setBgBusy(false);
    }
  };
  return (
    <div data-testid={`brand-asset-${asset.id}`} className={`relative rounded-xl border bg-white overflow-hidden flex flex-col ${selected ? "border-fuchsia-400 ring-2 ring-fuchsia-500/20" : "border-neutral-200"}`}>
      {onToggleSelect && url && (
        <input
          type="checkbox"
          data-testid={`brand-select-${asset.id}`}
          checked={selected}
          onChange={onToggleSelect}
          onClick={(e) => e.stopPropagation()}
          className="absolute top-2 left-2 z-10 h-5 w-5 accent-fuchsia-600 cursor-pointer rounded shadow bg-white/90"
        />
      )}
      {asset.file_kind === "image" && url && (
        <div className="h-24 sm:h-36 bg-[repeating-conic-gradient(#f5f5f5_0%_25%,white_0%_50%)] bg-[length:16px_16px] flex items-center justify-center border-b border-neutral-100">
          <img src={url} alt="" loading="lazy" className="max-h-20 sm:max-h-32 max-w-full object-contain p-2" />
        </div>
      )}
      {asset.file_kind === "video" && url && (
        <video src={`${url}#t=0.1`} playsInline className="h-24 sm:h-36 w-full object-cover bg-neutral-900 border-b border-neutral-100" muted controls preload="metadata" />
      )}
      <div className="p-2.5 sm:p-3.5 flex-1 flex flex-col">
        <p className="text-[13px] sm:text-sm font-semibold text-neutral-900 truncate">{asset.name}</p>
        {asset.description && <p className="text-[12px] text-neutral-500 mt-0.5 leading-snug line-clamp-2 sm:line-clamp-none">{asset.description}</p>}
        {asset.text_content && (
          <pre className="mt-2 text-[12px] text-neutral-700 bg-neutral-50 border border-neutral-100 rounded-lg p-2.5 whitespace-pre-wrap font-sans max-h-24 sm:max-h-32 overflow-y-auto">{asset.text_content}</pre>
        )}
        {videoCatOptions && isAdmin && onSubcategory && (
          <select
            data-testid={`brand-asset-subcat-${asset.id}`}
            value={asset.subcategory && videoCatOptions.includes(asset.subcategory) ? asset.subcategory : "Sonstiges"}
            onChange={(e) => onSubcategory(asset, e.target.value)}
            className="mt-2 w-full h-8 px-2 rounded-lg border border-neutral-200 text-[12px] text-neutral-600 bg-white"
          >
            {videoCatOptions.map((n) => <option key={n} value={n}>{n}</option>)}
            <option value="Sonstiges">Sonstiges</option>
          </select>
        )}
        <div className="flex items-center gap-1.5 mt-2.5 sm:mt-3 pt-2 border-t border-neutral-100">
          {url && (
            <a data-testid={`brand-download-${asset.id}`} href={url} target="_blank" rel="noopener noreferrer" download
              className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-neutral-700 border border-neutral-200 px-2 sm:px-2.5 py-1.5 rounded-lg hover:bg-neutral-50">
              <Download className="h-3.5 w-3.5" /> <span className="hidden sm:inline">Download</span>
            </a>
          )}
          {asset.text_content && (
            <button data-testid={`brand-copy-text-${asset.id}`} onClick={copyText}
              className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-neutral-700 border border-neutral-200 px-2 sm:px-2.5 py-1.5 rounded-lg hover:bg-neutral-50">
              <Copy className="h-3.5 w-3.5" /> <span className="hidden sm:inline">Kopieren</span>
            </button>
          )}
          {asset.file_kind === "image" && url && onRefresh && (
            <button data-testid={`brand-removebg-${asset.id}`} onClick={removeBg} disabled={bgBusy} title="Hintergrund per KI entfernen"
              className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-violet-700 border border-violet-200 bg-violet-50 px-2 sm:px-2.5 py-1.5 rounded-lg hover:bg-violet-100 disabled:opacity-50">
              {bgBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Scissors className="h-3.5 w-3.5" />} <span className="hidden sm:inline">{bgBusy ? "Stellt frei…" : "Freistellen"}</span>
            </button>
          )}
          <span className="ml-auto text-[11px] text-neutral-400 truncate hidden sm:inline">{asset.uploaded_by_name}</span>
          {isAdmin && (
            <button data-testid={`brand-delete-${asset.id}`} onClick={() => onDelete(asset.id)}
              className="h-7 w-7 shrink-0 rounded-lg flex items-center justify-center text-neutral-400 hover:text-red-600 hover:bg-red-50">
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function AddAssetDialog({ open, onOpenChange, accountKey, defaultCategory, onCreated }) {
  const [category, setCategory] = useState(defaultCategory);
  const [saving, setSaving] = useState(false);
  const [spOpen, setSpOpen] = useState(false);
  const [videoCats, setVideoCats] = useState({ defaults: [], custom: [] });
  const { user } = useAuth();
  const [form, setForm] = useState({ name: "", description: "", media_url: "", file_kind: "", hex: "#7c3aed", text_content: "", subcategory: "" });
  const f = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  useEffect(() => { if (open) { setCategory(defaultCategory); setForm({ name: "", description: "", media_url: "", file_kind: "", hex: "#7c3aed", text_content: "", subcategory: "" }); } }, [open, defaultCategory]);

  useEffect(() => {
    if (open && category === "video")
      api.get("/brand/video-categories", { params: { account: accountKey } }).then(({ data }) => setVideoCats(data)).catch(() => {});
  }, [open, category, accountKey]);

  const addVideoCat = async () => {
    const name = window.prompt("Name der neuen Video-Kategorie:");
    if (!name?.trim()) return;
    try {
      const { data } = await api.post("/brand/video-categories", { account_key: accountKey, name: name.trim() });
      setVideoCats((s) => ({ ...s, custom: [...s.custom, data].sort((a, b) => a.name.localeCompare(b.name)) }));
      f("subcategory", data.name);
      toast.success(`Kategorie „${data.name}" angelegt ✅`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Kategorie konnte nicht angelegt werden");
    }
  };

  const acceptFor = { logo: "image/*,.svg", font: ".ttf,.otf,.woff,.woff2,.zip", video: "video/*", preset: "" };

  const submit = async () => {
    if (!form.name.trim()) return toast.error("Bitte einen Namen angeben");
    if (category === "color" && !/^#[0-9a-fA-F]{3,8}$/.test(form.hex)) return toast.error("Bitte einen gültigen Hex-Wert angeben");
    if (["logo", "font", "video"].includes(category) && !form.media_url) return toast.error("Bitte eine Datei hochladen");
    if (category === "preset" && !form.media_url && !form.text_content.trim()) return toast.error("Bitte Datei hochladen oder Text einfügen");
    setSaving(true);
    try {
      await api.post("/brand/assets", { account_key: accountKey, category, ...form });
      toast.success("Asset gespeichert ✨");
      onCreated();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[88vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-head">Brand-Asset hinzufügen</DialogTitle>
          <DialogDescription>Steht danach allen Creatorn dieses Kanals zur Verfügung.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3.5">
          <Field label="Kategorie">
            <select data-testid="brand-form-category" value={category} onChange={(e) => setCategory(e.target.value)} className={cls}>
              {CATEGORIES.filter((c) => c.key !== "canva").map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
            </select>
          </Field>
          <Field label="Name">
            <input data-testid="brand-form-name" value={form.name} onChange={(e) => f("name", e.target.value)}
              placeholder={category === "color" ? "z. B. Primärfarbe" : "z. B. Logo weiß (Story)"} className={cls} />
          </Field>
          {category === "color" && (
            <Field label="Farbwert">
              <div className="flex items-center gap-2">
                <input type="color" data-testid="brand-form-color-picker" value={form.hex} onChange={(e) => f("hex", e.target.value)} className="h-10 w-12 rounded-lg border border-neutral-200 cursor-pointer p-1" />
                <input data-testid="brand-form-hex" value={form.hex} onChange={(e) => f("hex", e.target.value)} className={`${cls} font-mono`} />
              </div>
            </Field>
          )}
          {["logo", "font", "video", "preset"].includes(category) && (
            <div className="rounded-lg border border-neutral-200 p-3">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <span className="text-xs font-semibold uppercase tracking-wider text-neutral-500">
                  Datei{category === "preset" ? " (optional)" : ""}
                </span>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    data-testid="brand-form-sharepoint-btn"
                    onClick={() => setSpOpen(true)}
                    className="inline-flex items-center gap-1.5 h-8 px-2.5 rounded-lg bg-sky-50 border border-sky-200 text-[11px] font-bold text-sky-700 hover:bg-sky-100"
                  >
                    <Cloud className="h-3.5 w-3.5" /> Aus SharePoint
                  </button>
                  <FileUpload accept={acceptFor[category] || undefined} label="Hochladen"
                    onUploaded={(d) => { f("media_url", d.url); f("file_kind", d.kind); }} />
                </div>
              </div>
              <SharePointPickerDialog
                open={spOpen}
                onClose={() => setSpOpen(false)}
                onPicked={(att) => {
                  f("media_url", att.url);
                  f("file_kind", att.kind);
                  if (!form.name.trim()) f("name", att.filename.replace(/\.[^.]+$/, ""));
                  toast.success(`„${att.filename}" aus SharePoint übernommen ✅`);
                }}
              />
              {form.media_url && form.file_kind === "image" && (
                <div className="mt-2 rounded-md overflow-hidden border border-neutral-200">
                  <img src={resolveMediaUrl(form.media_url)} alt="" className="w-full max-h-40 object-contain bg-neutral-50" />
                </div>
              )}
              {form.media_url && form.file_kind !== "image" && (
                <p className="mt-2 text-[12px] text-emerald-600 font-medium">Datei hochgeladen ✓</p>
              )}
            </div>
          )}
          {category === "video" && (
            <Field label="Video-Kategorie">
              <div className="flex items-center gap-2">
                <select data-testid="brand-form-subcategory" value={form.subcategory} onChange={(e) => f("subcategory", e.target.value)} className={cls}>
                  <option value="">Sonstiges</option>
                  {videoCats.defaults.map((n) => <option key={n} value={n}>{n}</option>)}
                  {videoCats.custom.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
                </select>
                {user?.role === "admin" && (
                  <button type="button" data-testid="brand-form-add-videocat" onClick={addVideoCat} title="Neue Kategorie anlegen"
                    className="shrink-0 h-10 px-3 rounded-lg border border-neutral-200 text-sm font-semibold text-neutral-600 hover:bg-neutral-50">
                    +
                  </button>
                )}
              </div>
            </Field>
          )}
          {category === "preset" && (
            <Field label="Text-Vorlage (z. B. Caption-Template, Hashtag-Set)">
              <textarea data-testid="brand-form-text" rows={4} value={form.text_content} onChange={(e) => f("text_content", e.target.value)}
                placeholder={"#bruchköbel #lokal …"} className={`${cls} h-auto py-2 resize-none`} />
            </Field>
          )}
          <Field label="Beschreibung / Verwendungsregel (optional)">
            <input data-testid="brand-form-description" value={form.description} onChange={(e) => f("description", e.target.value)}
              placeholder="z. B. Nur für Headlines verwenden" className={cls} />
          </Field>
        </div>
        <DialogFooter>
          <button data-testid="brand-form-save" onClick={submit} disabled={saving}
            className="inline-flex items-center gap-2 bg-neutral-900 text-white text-sm font-medium px-4 py-2 rounded-lg hover:bg-neutral-800 disabled:opacity-50">
            {saving && <Loader2 className="h-4 w-4 animate-spin" />} Speichern
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CanvaSection({ accountKey, isAdmin, templates, onTemplatesChanged, onRemoveTemplate }) {
  const [status, setStatus] = useState(null);
  const [designs, setDesigns] = useState([]);
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(null);
  const [tplOpen, setTplOpen] = useState(false);
  const [tplForm, setTplForm] = useState({ name: "", canva_url: "" });
  const [tplSaving, setTplSaving] = useState(false);

  useEffect(() => { api.get("/canva/status").then(({ data }) => setStatus(data)).catch(() => setStatus({ configured: false })); }, []);

  const loadDesigns = async (q = "") => {
    setLoading(true);
    try {
      const { data } = await api.get("/canva/designs", { params: q ? { query: q } : {} });
      setDesigns(data.items || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Designs konnten nicht geladen werden");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (status?.connected) loadDesigns(); }, [status?.connected]); // eslint-disable-line react-hooks/exhaustive-deps

  const connect = () => {
    const token = localStorage.getItem("rl_token");
    window.location.href = `${BACKEND_URL}/api/auth/canva/login?token=${encodeURIComponent(token)}`;
  };

  const disconnect = async () => {
    await api.delete("/canva/disconnect");
    toast.success("Canva getrennt");
    setStatus((s) => ({ ...s, connected: false }));
    setDesigns([]);
  };

  const importDesign = async (design, format) => {
    setImporting(design.id);
    try {
      await api.post(`/canva/designs/${design.id}/import`, {
        account_key: accountKey, format, title: design.title,
        content_type: format === "mp4" ? "reel" : "post",
      }, { timeout: 120000 });
      toast.success(`„${design.title}" als Idee in die Pipeline übernommen ✨`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Import fehlgeschlagen");
    } finally {
      setImporting(null);
    }
  };

  const saveTemplate = async () => {
    if (!tplForm.name.trim() || !tplForm.canva_url.trim()) return toast.error("Name und Canva-Link angeben");
    setTplSaving(true);
    try {
      await api.post("/brand/assets", { account_key: accountKey, category: "canva_template", ...tplForm });
      toast.success("Vorlagen-Link gespeichert");
      setTplOpen(false);
      setTplForm({ name: "", canva_url: "" });
      onTemplatesChanged();
    } catch {
      toast.error("Speichern fehlgeschlagen");
    } finally {
      setTplSaving(false);
    }
  };

  if (!status) return <div className="py-16 flex justify-center text-neutral-400"><Loader2 className="h-6 w-6 animate-spin" /></div>;

  if (!status.configured) {
    return (
      <div data-testid="canva-not-configured" className="rounded-xl border border-dashed border-neutral-300 bg-neutral-50 p-6 max-w-2xl">
        <h3 className="font-head font-semibold text-neutral-900 flex items-center gap-2"><Link2 className="h-4 w-4 text-fuchsia-500" /> Canva anschließen</h3>
        <p className="text-sm text-neutral-600 mt-2">
          Die Canva-Anbindung ist vorbereitet — es fehlen nur noch die Zugangsdaten eurer Canva-Integration. So geht's (ca. 5 Minuten):
        </p>
        <ol className="text-sm text-neutral-600 mt-3 space-y-1.5 list-decimal list-inside">
          <li>Mit dem Team-Canva-Konto auf <a href="https://www.canva.com/developers/integrations/connect-api" target="_blank" rel="noopener noreferrer" className="text-fuchsia-700 font-medium underline">canva.com/developers</a> anmelden</li>
          <li>„Create an integration" → Typ „Public" oder „Private" wählen</li>
          <li>Unter <b>Scopes</b> aktivieren: design:meta:read, design:content:read, asset:read, profile:read</li>
          <li>Unter <b>Authentication</b> diese Redirect-URL eintragen:<br />
            <code className="text-[12px] bg-white border border-neutral-200 rounded px-1.5 py-0.5 mt-1 inline-block break-all">{BACKEND_URL}/api/auth/canva/callback</code>
          </li>
          <li><b>Client ID</b> und <b>Client Secret</b> kopieren und hier im Chat an mich schicken</li>
        </ol>
      </div>
    );
  }

  if (!status.connected) {
    return (
      <div data-testid="canva-not-connected" className="rounded-xl border border-neutral-200 bg-white p-6 max-w-xl text-center">
        <Link2 className="h-8 w-8 text-fuchsia-500 mx-auto" />
        <h3 className="font-head font-semibold text-neutral-900 mt-3">Mit dem Team-Canva-Konto verbinden</h3>
        <p className="text-sm text-neutral-500 mt-1">Einmal verbinden — danach können alle Creator Designs direkt in die Pipeline übernehmen.</p>
        <button data-testid="canva-connect-btn" onClick={connect}
          className="mt-4 inline-flex items-center gap-2 bg-[#7d2ae8] text-white text-sm font-semibold px-5 py-2.5 rounded-lg hover:opacity-90">
          <Link2 className="h-4 w-4" /> Mit Canva verbinden
        </button>
      </div>
    );
  }

  return (
    <div data-testid="canva-connected" className="space-y-6">
      <div className="flex flex-wrap items-center gap-3">
        <span className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 px-2.5 py-1 rounded-full">
          <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" /> Verbunden{status.display_name ? ` · ${status.display_name}` : ""}
        </span>
        <div className="relative flex-1 min-w-[220px] max-w-sm">
          <Search className="h-4 w-4 text-neutral-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            data-testid="canva-search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && loadDesigns(query)}
            placeholder="Designs durchsuchen…"
            className={`${cls} pl-9`}
          />
        </div>
        <button data-testid="canva-refresh" onClick={() => loadDesigns(query)}
          className="inline-flex items-center gap-1.5 text-sm font-medium text-neutral-700 border border-neutral-200 px-3 py-2 rounded-lg hover:bg-neutral-50">
          <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} /> Aktualisieren
        </button>
        {isAdmin && (
          <button data-testid="canva-disconnect" onClick={disconnect}
            className="inline-flex items-center gap-1.5 text-[12px] font-medium text-neutral-400 hover:text-red-600">
            <Unplug className="h-3.5 w-3.5" /> Trennen
          </button>
        )}
      </div>

      <div>
        <div className="flex items-center justify-between">
          <h3 className="font-head font-semibold text-neutral-900 text-[15px]">Vorlagen-Schnellzugriff</h3>
          <button data-testid="canva-add-template" onClick={() => setTplOpen(true)}
            className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-fuchsia-700 hover:text-fuchsia-800">
            <Plus className="h-3.5 w-3.5" /> Vorlagen-Link hinzufügen
          </button>
        </div>
        {templates.length === 0 ? (
          <p className="text-[13px] text-neutral-400 mt-2">Noch keine Vorlagen verlinkt. Team-Vorlagen aus Canva hier ablegen — ein Klick öffnet sie direkt im Editor.</p>
        ) : (
          <div className="flex flex-wrap gap-2 mt-2">
            {templates.map((t) => (
              <span key={t.id} className="inline-flex items-center rounded-lg border border-neutral-200 bg-white overflow-hidden group">
                <a data-testid={`canva-template-${t.id}`} href={t.canva_url} target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 text-[13px] font-medium text-neutral-800 px-3 py-2 hover:bg-fuchsia-50">
                  <ExternalLink className="h-3.5 w-3.5 text-fuchsia-500" /> {t.name}
                </a>
                {isAdmin && (
                  <button onClick={() => onRemoveTemplate(t.id)} className="px-2 py-2 text-neutral-300 hover:text-red-500 border-l border-neutral-100">
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                )}
              </span>
            ))}
          </div>
        )}
      </div>

      <div>
        <h3 className="font-head font-semibold text-neutral-900 text-[15px]">Team-Designs</h3>
        {loading ? (
          <div className="py-12 flex justify-center text-neutral-400"><Loader2 className="h-6 w-6 animate-spin" /></div>
        ) : designs.length === 0 ? (
          <p className="text-[13px] text-neutral-400 mt-2">Keine Designs gefunden.</p>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mt-3">
            {designs.map((d) => (
              <div key={d.id} data-testid={`canva-design-${d.id}`} className="rounded-xl border border-neutral-200 bg-white overflow-hidden flex flex-col">
                <div className="h-36 bg-neutral-100 flex items-center justify-center overflow-hidden">
                  {d.thumbnail
                    ? <img src={d.thumbnail} alt="" className="w-full h-full object-cover" />
                    : <ImageIcon className="h-8 w-8 text-neutral-300" />}
                </div>
                <div className="p-3 flex-1 flex flex-col">
                  <p className="text-[13px] font-semibold text-neutral-900 leading-snug line-clamp-2">{d.title}</p>
                  <div className="flex items-center gap-1.5 mt-auto pt-3">
                    <button
                      data-testid={`canva-import-${d.id}`}
                      onClick={() => importDesign(d, "png")}
                      disabled={importing === d.id}
                      className="inline-flex items-center gap-1.5 text-[12px] font-semibold bg-neutral-900 text-white px-2.5 py-1.5 rounded-lg hover:bg-neutral-800 disabled:opacity-50"
                    >
                      {importing === d.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
                      In Pipeline
                    </button>
                    {d.edit_url && (
                      <a href={d.edit_url} target="_blank" rel="noopener noreferrer"
                        className="h-7 w-7 rounded-lg border border-neutral-200 flex items-center justify-center text-neutral-500 hover:bg-neutral-50">
                        <ExternalLink className="h-3.5 w-3.5" />
                      </a>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <Dialog open={tplOpen} onOpenChange={setTplOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-head">Canva-Vorlagen-Link</DialogTitle>
            <DialogDescription>Link zu einer Team-Vorlage in Canva — für alle Creator per One-Klick erreichbar.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3.5">
            <Field label="Name">
              <input data-testid="canva-tpl-name" value={tplForm.name} onChange={(e) => setTplForm((s) => ({ ...s, name: e.target.value }))}
                placeholder="z. B. Story-Vorlage Event" className={cls} />
            </Field>
            <Field label="Canva-Link">
              <input data-testid="canva-tpl-url" value={tplForm.canva_url} onChange={(e) => setTplForm((s) => ({ ...s, canva_url: e.target.value }))}
                placeholder="https://www.canva.com/design/…" className={cls} />
            </Field>
          </div>
          <DialogFooter>
            <button data-testid="canva-tpl-save" onClick={saveTemplate} disabled={tplSaving}
              className="inline-flex items-center gap-2 bg-neutral-900 text-white text-sm font-medium px-4 py-2 rounded-lg hover:bg-neutral-800 disabled:opacity-50">
              {tplSaving && <Loader2 className="h-4 w-4 animate-spin" />} Speichern
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-xs font-medium text-neutral-600">{label}</label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
